<template>
  <div class="pa-3">
    <div class="ml-2 mb-2">
      <v-icon
        v-if="!isMinimized"
        size="18"
        @click="handleClickMinimize"
        title="Hide results"
        >fas fa-arrow-left</v-icon
      >
      <v-icon
        v-if="isMinimized"
        size="18"
        @click="handleClickMinimize"
        title="Show results"
        >fas fa-arrow-right</v-icon
      >
    </div>
    <div v-if="!isMinimized">
      <Loader
        v-if="!details"
        :text="loaderText"
        size="small"
        class="mt-5"
      ></Loader>
      <v-container v-if="details" class="details-container">
        <v-row>
          <v-col cols="12" class="pt-0">
            <h1>{{ selectedLocation.name }}</h1>
          </v-col>
        </v-row>

        <v-row v-if="zones && zones.length">
          <v-col cols="12">
            <v-select
              v-model="selectedZones"
              :items="zones"
              item-text="name"
              item-value="id"
              label="Compare Zones"
              clearable
              small-chips
              return-object
              class="zones-list"
              @change="handleChangeZone"
            ></v-select>
          </v-col>
        </v-row>

        <RiskRating
          v-if="ratings && !loadingRisks"
          :ratings="ratings"
          :selectedPrimaryRisk="selectedPrimaryRisk"
          :selectedSubRisk="selectedSubRisk"
          @click="handleClickRiskRating"
        ></RiskRating>

        <div v-for="(zone, index) in selectedZonesComputed" :key="index">
          <hr />
          <RiskRating
            :title="'Zone: ' + zone.name"
            v-if="ratings && !loadingRisks && zone.ratings"
            :ratings="zone.ratings"
            :selectedPrimaryRisk="selectedPrimaryRisk"
            :selectedSubRisk="selectedSubRisk"
            :parentRatings="ratings"
            @click="handleClickRiskRating"
          ></RiskRating>
        </div>

        <v-row v-if="score">
          <v-col cols="12">
            <GridHighlight
              type="stability"
              :dark="false"
              :text="score"
              :minScore="minScore"
              :maxScore="maxScore"
              :scoreDelta="scoreDelta"
              :large-text="true"
              class="ml-3"
            ></GridHighlight>
          </v-col>
        </v-row>

        <v-row v-if="body">
          <v-col cols="12" class="pt-0 content-container">
            <strong class="details-heading">{{ title }}</strong>
            <v-card
              v-show="showBodySections"
              ref="bodySections"
              v-html="getCleanBody(body)"
              class="mt-2 pa-3"
            >
            </v-card>
          </v-col>
        </v-row>

        <RisksUpdates :iso3-code="iso3Code" :category="category"></RisksUpdates>

        <RisksScenarios :background="details"></RisksScenarios>

        <div
          v-show="showBodySections"
          v-if="
            bodyItems &&
            bodyItems.length &&
            bodyItems[0].Title &&
            bodyItems[0].Body
          "
        >
          <v-row v-for="(bodyItem, index) in bodyItems" :key="index">
            <v-col cols="12" class="pt-3 content-container">
              <strong class="details-heading">{{ bodyItem.Title }}</strong>
              <v-card
                ref="bodyItemSections"
                v-html="getCleanBody(bodyItem.Body)"
                class="mt-2 pa-3"
              ></v-card>
            </v-col>
          </v-row>
        </div>
      </v-container>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import lodashGet from "lodash/get";
import lodashMap from "lodash/map";
import lodashMethod from "lodash/method";
import lodashDebounce from "lodash/debounce";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashFind from "lodash/find";
import lodashRemove from "lodash/remove";

import {
  getCountryDetails,
  getRiskRatings,
  highlightZoneOnMap,
} from "@/services/controlRisks/risks.service";
import { getStabilityByCountry } from "@/services/grids/stability.service";
import { addTargetToLinks } from "@/common/basic.utilities";
import { getTodayStartEndDate } from "@/common/date.utilities";
import { MAIN_MAP_ID } from "@/common/constants/maps";
import { MODULE_PREFIX_MAP } from "@/store";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";
import { pageTrackerLogger } from "@/common/pageTracker";

const EMITTED_ACTIONS = {
  CLICK_MINIMIZE: "click-minimize",
  CLICK_RISK_RATING: "click-risk-rating",
};

export default {
  name: "RisksContent",
  components: {
    Loader: () => import("@/components/common/Loader"),
    RiskRating: () => import("@/components/controlRisks/risks/RiskRating"),
    RisksUpdates: () => import("@/components/controlRisks/risks/RisksUpdates"),
    RisksScenarios: () =>
      import("@/components/controlRisks/risks/RisksScenarios"),
    GridHighlight: () => import("@/components/grids/GridHighlight"),
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    selectedLocation: {
      type: Object,
    },
    isMinimized: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      details: null,
      updates: null,
      body: null,
      title: null,
      bodyItems: null,
      bodyElems: null,
      bodyElemsInitialShown: 2,
      showBodySections: false,
      loadingRisks: false,
      zones: null,
      selectedZones: null,
      stability: null,
      risks: null,
      riskType: null,
      ratings: null,
      riskTypes: [
        { id: null, name: "None" },
        { id: "dd8fac8d-6f95-41e5-9f8e-9878037a2969", name: "Political" },
        { id: "a4d0c0e0-5eb3-4fc1-9ffc-da8072c3dbbe", name: "Operational" },
        { id: "a19bfe35-22d5-4361-99d6-2f65fc7df184", name: "Security" },
      ],
      category: null,
      selectedPrimaryRisk: null,
      selectedPrimaryRiskOverview: null,
      selectedSubRisk: null,
    };
  },
  computed: {
    ...mapGetters({
      maps: MODULE_PREFIX_MAP + "maps",
    }),
    map() {
      return this.maps[MAIN_MAP_ID];
    },
    iso3Code() {
      if (this.selectedLocation && this.selectedLocation.iso3Code) {
        return this.selectedLocation.iso3Code;
      }
      return null;
    },
    loaderText() {
      const selectedText =
        this.selectedLocation && this.selectedLocation.name
          ? this.selectedLocation.name
          : "";
      return `Loading ${selectedText} details`;
    },
    score() {
      let result = null;
      if (lodashGet(this.stability, "Item.count")) {
        result = this.stability.Item.count;
      }

      if (typeof result === "number") {
        // need to use Math.floor because the map color styling doesn't round at all so
        // 59.99 still will show yellow instead of green which is 60
        result = Math.floor(result);
      }
      return result;
    },
    minScore() {
      if (
        typeof lodashGet(this.stability, "Item.stats.percentiles.very_low") ===
        "number"
      ) {
        const min = this.stability.Item.stats.percentiles.very_low;
        return min < 1 ? min : Math.floor(min);
      }
      return null;
    },
    maxScore() {
      if (
        typeof lodashGet(this.stability, "Item.stats.percentiles.very_high") ===
        "number"
      ) {
        return Math.floor(this.stability.Item.stats.percentiles.very_high);
      }
      return null;
    },
    scoreDelta() {
      const scoreYesterday = lodashGet(this.stability, "Item.trend[0]");
      if (scoreYesterday && this.score) {
        return Math.floor(this.score - scoreYesterday);
      }
      return null;
    },
    selectedZonesComputed() {
      if (this.selectedZones) {
        if (Array.isArray(this.selectedZones)) {
          return this.selectedZones;
        } else {
          return [this.selectedZones];
        }
      }
      return [];
    },
  },
  methods: {
    handleClickMinimize() {
      this.$emit(EMITTED_ACTIONS.CLICK_MINIMIZE);
    },
    handleClickRiskRating(rating, isSubRisk) {
      this.getContent(rating, isSubRisk);
      if (!isSubRisk) {
        this.selectedSubRisk = null;
        this.selectedPrimaryRisk = rating;
        this.selectedPrimaryRiskOverview = null;
      } else {
        this.selectedSubRisk = rating;
      }
      this.category = rating;
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.control_risk_risk_ratings_overview,
        EVENT_ACTIONS.clicked,
        "Risk Ratings - " + rating.type
      );
    },
    async getStability() {
      this.stability = null;
      if (this.selectedLocation) {
        const params = {
          aoi: this.selectedLocation.iso2Code,
          dataLensType: "allgdeltv2",
          gridType: "stability",
          stabilityType: "country",
          start: getTodayStartEndDate().start,
          syncTileScores: true,
        };
        const result = await getStabilityByCountry(params);
        if (result) {
          this.stability = result;
        }
      }
    },
    async getContent(category, isSubRisk) {
      this.showBodySections = false;
      this.details = await getCountryDetails(this.selectedLocation.iso3Code);
      if (this.details) {
        if (category && category.type) {
          if (lodashGet(this.details, "RiskContent")) {
            this.details.RiskContent.forEach((item) => {
              if (item.RiskCategories) {
                if (!isSubRisk) {
                  if (
                    item.RiskCategories.length === 1 &&
                    item.RiskCategories[0].toLowerCase() ===
                      category.type.toLowerCase()
                  ) {
                    this.title =
                      category.type.toLowerCase() !== item.Title.toLowerCase()
                        ? `${category.type} ${item.Title}`
                        : `${category.type} Overview`;
                    this.body = item.Body;
                    this.selectedPrimaryRiskOverview = lodashCloneDeep(
                      this.body
                    );
                  }
                } else {
                  const categories = lodashMap(
                    item.RiskCategories,
                    lodashMethod("toLowerCase")
                  );
                  if (
                    categories.length > 1 &&
                    categories.includes(category.type.toLowerCase())
                  ) {
                    this.title = item.Title;
                    let body = item.Body;
                    if (body === `<p style="background: white;">&nbsp;</p>`) {
                      body = this.selectedPrimaryRiskOverview;
                    }
                    this.body = body;
                  }
                }
              }
            });
          }
          this.bodyItems = null;
        } else {
          if (lodashGet(this.details, "Overview.Title")) {
            this.title = this.details.Overview.Title;
          }
          if (lodashGet(this.details, "Overview.Body")) {
            this.body = this.details.Overview.Body;
          }
          if (lodashGet(this.details, "Overview.BodyItems")) {
            const bodyItems = this.details.Overview.BodyItems;
            const evacBody = lodashFind(bodyItems, [
              "Title",
              "Evacuation Summary",
            ]);
            lodashRemove(bodyItems, evacBody);
            this.bodyItems = bodyItems;
          }
        }
      }
      this.addMoreLessLogic();
    },
    async getRisks() {
      this.loadingRisks = true;
      const risksGeos = await getRiskRatings({
        code: this.selectedLocation.iso3Code,
      });
      if (risksGeos && Array.isArray(risksGeos)) {
        for (let i = 0; i < risksGeos.length; i++) {
          const risksGeo = risksGeos[i];
          if (
            lodashGet(risksGeo, "iso3Code") &&
            risksGeo.iso3Code.toLowerCase() ===
              this.selectedLocation.iso3Code.toLowerCase()
          ) {
            this.ratings = risksGeo.ratings ? risksGeo.ratings : null;
            this.zones = risksGeo.riskZones ? risksGeo.riskZones : null;
            break;
          }
        }
      }
      this.loadingRisks = false;
    },
    getCleanBody(body) {
      if (body) {
        return addTargetToLinks(body);
      }
      return "";
    },
    addMoreLessLogic() {
      lodashDebounce(() => {
        this.bodyElems = [];

        const elemBody = this.$refs.bodySections;
        this.bodyElems.push(elemBody);

        const elemBodyItem = this.$refs.bodyItemSections;
        if (elemBodyItem && Array.isArray(elemBodyItem)) {
          const items = [];
          elemBodyItem.forEach((item) => {
            if (
              item &&
              item.$el &&
              item.$el.children &&
              item.$el.children.length > 1
            ) {
              items.push(item);
            }
          });
          this.bodyElems = this.bodyElems.concat(items);
        }
        if (this.bodyElems && Array.isArray(this.bodyElems)) {
          this.bodyElems.forEach((elem) => {
            if (elem && elem.$el) {
              let elemsShown = 2;
              if (elem.$el.children[0].innerText.split(" ").length > 30) {
                elemsShown = 1;
              }

              let showMoreExists = false;
              elem.$el.children.forEach((elm) => {
                if (elm.className.includes("show-more")) {
                  showMoreExists = true;
                }
              });

              if (elem.$el.children.length > elemsShown && !showMoreExists) {
                const divMore = document.createElement("DIV");
                divMore.innerHTML = "Show More";
                divMore.className = "show-more mt-3 mr-3 text-right";
                divMore.style.cursor = "pointer";
                divMore.onclick = () => {
                  this.showHideBodyChildren(elem, false, elemsShown);
                };
                elem.$el.appendChild(divMore);

                const divLess = document.createElement("DIV");
                divLess.innerHTML = "Show Less";
                divLess.className = "show-less mt-3 mr-3 text-right";
                divLess.style.display = "none";
                divLess.style.cursor = "pointer";
                divLess.onclick = () => {
                  this.showHideBodyChildren(elem, true, elemsShown);
                };
                elem.$el.appendChild(divLess);
              }

              // add click event to all the images to open them in separate tab
              const imgs = elem.$el.querySelectorAll("img");
              Array.from(imgs).forEach((img) => {
                img.style.cursor = "pointer";
                img.addEventListener("click", () => {
                  const w = window.open("", "_blank");
                  w.document.write(img.outerHTML);
                });
              });

              this.showHideBodyChildren(elem, true, elemsShown);
            }
          });
        }
        this.showBodySections = true;
      }, 2000)();
    },
    showHideBodyChildren(elem, hide, elemsShown) {
      if (elem && elem.$el && elem.$el.children && elem.$el.children.length) {
        const len = elem.$el.children.length;
        for (let i = elemsShown; i < len; i++) {
          elem.$el.children[i].style.display = hide ? "none" : "block";
        }
        if (len > 1) {
          elem.$el.children[len - 2].style.display = hide ? "block" : "none";
        }
      }
      if (!hide) {
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.control_risk_risk_ratings_overview,
          EVENT_ACTIONS.expanded,
          "Security Overview - " + this.selectedLocation.name
        );
      }
    },
    handleChangeZone(zone) {
      this.getContent();
      if (zone && Array.isArray(zone)) {
        zone = zone[zone.length - 1];
      }
      if (zone && zone.id) {
        this.updates = null;
        highlightZoneOnMap({
          map: this.map,
          zoneCode: zone.code,
        });
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.control_risk_risk_ratings,
          EVENT_ACTIONS.clicked,
          "Subregion - " + zone.name
        );
      } else {
        this.getRisks();
      }
    },
    handlePageTrackerEvent(category, action, name) {
      let _paq = (window._paq = window._paq || []);
      _paq.push(["trackEvent", category, action, name]);
      pageTrackerLogger(
        "Page Tracker Event " +
          category +
          " " +
          action +
          " " +
          name +
          " logged."
      );
    },
  },
  watch: {
    selectedLocation: {
      handler() {
        if (this.selectedLocation && this.selectedLocation.iso3Code) {
          this.selectedPrimaryRisk = null;
          this.selectedPrimaryRiskOverview = null;
          this.selectedSubRisk = null;
          this.selectedZones = [];
          this.getStability();
          this.getContent();
          this.getRisks();
        }
      },
      immediate: true,
      deep: true,
    },
    riskType() {
      this.showBodySections = false;
      if (!this.riskType.id && lodashGet(this.details, "Overview.Body")) {
        this.body = this.details.Overview.Body;
      } else {
        if (
          this.details.RiskContent &&
          Array.isArray(this.details.RiskContent)
        ) {
          for (let i = 0; i < this.details.RiskContent.length; i++) {
            const riskCategories = this.details.RiskContent[i].RiskCategories;
            if (
              riskCategories.length === 1 &&
              riskCategories[0].toLowerCase() === this.riskType.toLowerCase()
            ) {
              this.body = this.details.RiskContent[i].Body;
            }
          }
        }
      }
      this.addMoreLessLogic();
    },
  },
  beforeDestroy() {
    this.selectedPrimaryRisk = null;
    this.selectedPrimaryRiskOverview = null;
    this.selectedSubRisk = null;
  },
};
</script>

<style>
.content-container img {
  max-width: 460px;
}
.zones-list .v-chip__content {
  max-width: 380px !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}
.zones-list .v-text-field__details {
  display: none !important;
}
</style>

<style scoped>
.details-container {
  height: 93vh;
  overflow-y: auto;
}
.details-heading {
  font-size: 20px;
}
.content-container {
  max-width: 530px !important;
}
.update-title {
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
}
.update-date {
  font-size: 12px;
}
</style>
